import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Root of a modal dialog (controls open state). */
export const Dialog = DialogPrimitive.Root

/** Element that opens the dialog when activated. */
export const DialogTrigger = DialogPrimitive.Trigger

/** Dimmed backdrop rendered behind the dialog panel. */
function DialogOverlay({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-50 bg-scrim', className)}
      {...props}
    />
  )
}

/** The centered dialog panel, portaled to the document body. */
export function DialogContent({
  ref,
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-2rem)] grid-cols-1 max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-overlay',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

/** Groups the dialog's title and description at the top of the panel. */
export function DialogHeader({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 text-left', className)}
      {...props}
    />
  )
}

/**
 * Groups the dialog's action buttons at the bottom of the panel: cancel on the
 * left, the confirming action on the right (stacked, confirm first, on mobile).
 */
export function DialogFooter({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  )
}

/** The accessible title of the dialog. */
export function DialogTitle({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        'text-title leading-tight font-semibold text-foreground',
        className,
      )}
      {...props}
    />
  )
}

/** The accessible description of the dialog. */
export function DialogDescription({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-control text-muted-foreground', className)}
      {...props}
    />
  )
}
